import { Box, Button, Icon, Theme, Typography } from "@outschool/backpack";
import { faBellConcierge } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { joinMembershipPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import { useNavigation } from "@outschool/ui-utils";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";

import useConcierge from "../../hooks/useConcierge";
import ConciergeChatModal from "../chat/ConciergeChatModal";
import {
  CONCIERGE_TRACKING_EVENTS,
  CONCIERGE_URL_PARAMS
} from "../concierge/constants";
import { ConciergeNavTab } from "./DesktopNavTabs";

interface ConciergeNavItemProps {
  minimalistic?: boolean;
  onClick?: () => void;
}

export const ConciergeNavItemDisplay = ({
  minimalistic = false,
  onClick
}: ConciergeNavItemProps) => {
  const { t } = useTranslation("client\\components\\nav\\ConciergeNavItem");
  const { isLoggedIn } = useSession();

  if (isLoggedIn) {
    return <ConciergeNavTab onClick={onClick} />;
  }

  return (
    <Box
      onClick={onClick}
      sx={(theme: Theme) => ({
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("md")]: {
          marginY: theme.spacing(10)
        }
      })}
    >
      <Button
        variant="link"
        sx={(theme: Theme) => ({
          padding: minimalistic ? "0px" : "10px 10px",
          border: "none",
          outline: "none",
          position: "relative",
          borderRadius: "30px",
          cursor: "pointer",
          zIndex: "1",
          color: "black",
          backgroundColor: "transparent",
          ":hover:not(:disabled)": {
            textDecoration: "none"
          },
          [theme.breakpoints.down("md")]: {
            display: "flex",
            flexDirection: "column-reverse",
            paddingY: theme.spacing(14),
            paddingX: minimalistic ? theme.spacing(5) : theme.spacing(20)
          },
          [theme.breakpoints.between("md", "lg")]: {
            padding: 0
          }
        })}
      >
        <Typography
          variant="body2"
          sx={(theme: Theme) => ({
            [theme.breakpoints.up("sm")]: {
              display: "none"
            }
          })}
        >
          {t("Concierge")}
        </Typography>
        {!minimalistic && (
          <Icon
            icon={faBellConcierge}
            sx={{ fontSize: "2rem", marginRight: "5px" }}
          />
        )}
        <Typography
          emphasized
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("sm")]: {
              display: "none"
            }
          })}
        >
          {t("Concierge")}
        </Typography>
      </Button>
    </Box>
  );
};

export const ConciergeNavItemContainer = ({
  children
}: {
  children: (props: { onClick: () => void }) => React.ReactElement;
}) => {
  const [conciergeChatModalOpen, setConciergeChatModalOpen] =
    useState<boolean>(false);
  const [shouldNavigateAfterClose, setShouldNavigateAfterClose] =
    useState(false);

  const trackEvent = useTrackEvent();
  const { isLoggedIn } = useSession();
  const { canAccessConcierge } = useConcierge();
  const location = useLocation();
  const navigate = useNavigation();
  const { enterLoginFlow } = useLoginFlowContext();

  const shouldOpenConcierge = location.search.includes(
    `${CONCIERGE_URL_PARAMS.OPEN_CONCIERGE}=true`
  );

  const isAuthTriggered = location.search.includes("authTrigger=concierge");

  const shouldOpenChatModal = useCallback(() => {
    return canAccessConcierge && !conciergeChatModalOpen;
  }, [canAccessConcierge, conciergeChatModalOpen]);

  const shouldTriggerLoginFlow = useCallback(() => {
    return !isLoggedIn && !isAuthTriggered;
  }, [isLoggedIn, isAuthTriggered]);

  const openModal = useCallback(() => {
    // Logged in user with concierge access - open chat modal
    if (shouldOpenChatModal()) {
      setConciergeChatModalOpen(true);
      trackEvent(CONCIERGE_TRACKING_EVENTS.MODAL_OPENED);
      return;
    }

    const urlAfterSignUp =
      location.pathname +
      (location.search
        ? `${location.search}&${CONCIERGE_URL_PARAMS.OPEN_CONCIERGE}=true`
        : `?${CONCIERGE_URL_PARAMS.OPEN_CONCIERGE}=true`);

    // Not logged in, - trigger login flow
    if (shouldTriggerLoginFlow()) {
      enterLoginFlow({
        authTrigger: AuthTrigger.CONCIERGE,
        newUrlAfterSignUp: urlAfterSignUp,
        newUrlAfterLogin: urlAfterSignUp
      });
      return;
    }
  }, [
    shouldOpenChatModal,
    shouldTriggerLoginFlow,
    enterLoginFlow,
    location.pathname,
    location.search,
    trackEvent
  ]);

  useEffect(() => {
    // if url contains open concierge, open modal
    if (!shouldOpenConcierge) {
      return;
    }

    openModal();
  }, [shouldOpenConcierge, openModal]);

  useEffect(() => {
    if (shouldNavigateAfterClose) {
      navigate(joinMembershipPath());
      setShouldNavigateAfterClose(false);
    }
  }, [shouldNavigateAfterClose, navigate]);

  return (
    <>
      {children({ onClick: () => openModal() })}
      <ConciergeChatModal
        isOpen={conciergeChatModalOpen}
        setIsModalOpen={setConciergeChatModalOpen}
      />
    </>
  );
};

export function ConciergeNavItem({
  minimalistic = false
}: {
  minimalistic?: boolean;
}) {
  const trackEvent = useTrackEvent();
  const handleNavClick = () => {
    trackEvent(CONCIERGE_TRACKING_EVENTS.NAV_ITEM_CLICKED);
  };
  return (
    <ConciergeNavItemContainer>
      {({ onClick }) => (
        <ConciergeNavItemDisplay
          minimalistic={minimalistic}
          onClick={() => {
            handleNavClick();
            onClick();
          }}
        />
      )}
    </ConciergeNavItemContainer>
  );
}
